import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label del checkbox */
  label?: string;
  /** Descripción adicional */
  description?: string;
  /** Si está en estado de error */
  error?: boolean;
  /** Mensaje de error */
  errorMessage?: string;
}

/**
 * Checkbox - Componente checkbox con estilos Tailwind Plus
 * 
 * Mejoras aplicadas:
 * - appearance-none con checkmark custom
 * - Border para definición visual
 * - Dark mode: bg-white/5 con border-white/10
 * - Checked: bg-primary-600 con checkmark SVG overlay
 * - focus-visible: ring-2 ring-offset-2
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, error, errorMessage, className, id, ...props }, ref) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substring(2, 9)}`;

    return (
      <div className="flex items-start gap-3">
        {/* Checkbox container with custom styling */}
        <div className="flex items-center h-6">
          <div className="relative flex items-center justify-center">
            <input
              {...props}
              ref={ref}
              id={checkboxId}
              type="checkbox"
              className={cn(
                // Base - appearance-none para control total
                'peer size-4 shrink-0 appearance-none rounded cursor-pointer',
                // Light mode - usando border en lugar de outline
                'bg-white border border-secondary-300',
                // Checked state
                'checked:bg-primary-600 checked:border-primary-600',
                // Focus - usando ring para el focus visible
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-600',
                // Dark mode
                'dark:bg-white/5 dark:border-white/10',
                'dark:checked:bg-primary-500 dark:checked:border-primary-500',
                'dark:focus-visible:ring-primary-500 dark:focus-visible:ring-offset-secondary-900',
                // Disabled
                'disabled:bg-secondary-100 disabled:border-secondary-200 disabled:cursor-not-allowed',
                'dark:disabled:bg-white/[0.02] dark:disabled:border-white/5',
                // Error
                error && 'border-danger-500 dark:border-danger-400',
                className
              )}
            />
            {/* Custom checkmark - visible when checked */}
            <svg
              className={cn(
                'pointer-events-none absolute size-3 text-white',
                'opacity-0 peer-checked:opacity-100',
                'transition-opacity'
              )}
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M3 8L6 11L11 3.5"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
        
        {/* Label and description */}
        {(label || description) && (
          <div className="text-sm leading-6">
            {label && (
              <label
                htmlFor={checkboxId}
                className={cn(
                  'font-medium text-secondary-900 dark:text-white',
                  props.disabled && 'opacity-50 cursor-not-allowed',
                  !props.disabled && 'cursor-pointer'
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-secondary-500 dark:text-secondary-400">{description}</p>
            )}
            {error && errorMessage && (
              <p className="text-danger-600 dark:text-danger-400 mt-1">{errorMessage}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
